import {calculateErrorMessage} from 'common/helpers'
import {useCallback, useMemo} from 'react'

import {ChatbotMode, isWalkthroughEnabled} from '../config/chatbotModeConfig'
import {sendVoiceChatMessage} from '../voice/helpers/voiceService'
import {StreamingMultipartParser} from '../voice/helpers/voiceStreamingParser'
import {decodeHeader} from '../voice/helpers/voiceUtils'
import {TranscriptLine, VoiceCallStatus} from './useVoiceCallState'
import {VoiceCallSharedRefs} from './useVoiceCallStateRegistry'

export const useVoiceCallApi = (
  refs: VoiceCallSharedRefs,
  stateActions: {
    setStatus: (s: VoiceCallStatus) => void
    setIsAiSpeaking: (s: boolean) => void
    setAwaitingResponse: (s: boolean) => void
    setTranscript: (fn: (prev: TranscriptLine[]) => TranscriptLine[]) => void
    setInterimUserTranscript: (t: string) => void
    setTranscriptionStage: (s: 'idle' | 'filler_transcribing' | 'answer_transcribing') => void
    reconcileUserTranscript: (t: string) => void
  },
  audioActions: {
    stopUtteranceRecording: () => Promise<{blob: Blob; mimeType: string}>
    playFillerAudio: (blob: Blob, unblock?: (r: string, imm?: boolean) => void) => Promise<void>
    playFullResponseAudio: (blob: Blob, unblock: any) => Promise<void>
    startListening: () => void
    startUtteranceRecording: () => void
    startProgressiveTranscript: (t: string) => string | null
    stopProgressiveTranscript: () => void
  },
  options: {
    getConversationHistory?: () => Array<{query: string; answer: string}>
    selectedVoice: string | null
    chatbotMode?: ChatbotMode
    getMenuSelectedPath?: () => string
  }
) => {
  const {
    setStatus,
    setIsAiSpeaking,
    setAwaitingResponse,
    setInterimUserTranscript,
    setTranscriptionStage,
    reconcileUserTranscript,
  } = stateActions

  /**
   * Swaps the browser recogniser's guess for the server's transcription of the same recording.
   * No-op while the transcript panel is off, since nothing was committed for it to replace.
   *
   * @param value - The transcription text.
   * @param isRawHeader - True when `value` came straight off a response header and is still
   *   percent-encoded. `sendVoiceChatMessage` already decodes the non-streaming case.
   */
  const applyServerTranscription = useCallback(
    (value?: string | null, isRawHeader = false) => {
      if (refs.transcriptionEnabledRef.current !== true) return
      const text = isRawHeader ? decodeHeader(value || '') : value || ''
      if (text.trim()) reconcileUserTranscript(text)
    },
    [refs, reconcileUserTranscript]
  )

  const blockListening = useCallback(
    (reason: string, allowInterrupts = false) => {
      if (refs.listeningBlockedRef.current) return
      refs.listeningBlockedRef.current = true
      refs.blockingReasonRef.current = reason
      refs.ignoreRecognizerDuringAiRef.current = !allowInterrupts
      refs.interruptListeningActiveRef.current = allowInterrupts
      refs.isAudioPlayingRef.current = true
    },
    [refs]
  )

  const finalizeAiTurn = useCallback(() => {
    setStatus('listening')
    const id = window.setTimeout(() => {
      try {
        if (refs.statusRef.current === 'listening' && !refs.mutedRef.current) {
          audioActions.startListening()
          audioActions.startUtteranceRecording()
        }
      } finally {
        refs.timeoutIdsRef.current.delete(id as any)
      }
    }, 500)
    refs.timeoutIdsRef.current.add(id as any)
  }, [refs, setStatus, audioActions])

  const unblockListening = useCallback(
    (reason: string, immediate = false, restart = false) => {
      if (!refs.listeningBlockedRef.current) return
      const doUnblock = () => {
        refs.listeningBlockedRef.current = false
        refs.blockingReasonRef.current = ''
        refs.ignoreRecognizerDuringAiRef.current = false
        refs.interruptListeningActiveRef.current = false
        refs.isAudioPlayingRef.current = false
        refs.hasVoiceActivityRef.current = false
        refs.realSpeechDetectedRef.current = false
        refs.consecutiveRealSpeechRef.current = 0
        refs.sustainedSpeechRef.current = false
        refs.voiceActivityBufferRef.current = []
        refs.lastAudioActivityRef.current = Date.now()
        refs.maxListeningTimeRef.current = Date.now()
        refs.noiseFloorRef.current = 0.01

        if (restart) {
          refs.responseRequestedRef.current = false
          finalizeAiTurn()
        }
      }
      if (immediate) doUnblock()
      else {
        const id = window.setTimeout(() => {
          try {
            doUnblock()
          } finally {
            refs.timeoutIdsRef.current.delete(id as any)
          }
        }, 400)
        refs.timeoutIdsRef.current.add(id as any)
      }
    },
    [refs, finalizeAiTurn]
  )

  const resetVoiceState = useCallback(
    (force = false) => {
      if (!force && (refs.isAudioPlayingRef.current || refs.listeningBlockedRef.current)) return
      refs.inFlightRef.current = false
      refs.responseRequestedRef.current = false
      refs.hasVoiceActivityRef.current = false
      refs.realSpeechDetectedRef.current = false
      refs.sustainedSpeechRef.current = false
      setAwaitingResponse(false)
      setIsAiSpeaking(false)
      if (refs.statusRef.current === 'responding') {
        finalizeAiTurn()
      }
    },
    [refs, setAwaitingResponse, setIsAiSpeaking, finalizeAiTurn]
  )

  const handleTransitionToResponding = useCallback(
    async (audioRefs: any) => {
      if (
        refs.inFlightRef.current ||
        refs.responseRequestedRef.current ||
        refs.listeningBlockedRef.current
      ) {
        return
      }
      refs.inFlightRef.current = true
      refs.responseRequestedRef.current = true
      let pendingWalkthroughData: any = null
      const hadVoiceActivity = refs.hasVoiceActivityRef.current
      const hadRealSpeech = refs.realSpeechDetectedRef.current
      const pendingUserInterim = refs.interimUserTranscriptRef.current || ''

      blockListening('transitioning to responding')
      const forceResetTimeout = setTimeout(() => {
        if (refs.listeningBlockedRef.current && refs.isAudioPlayingRef.current) return
        resetVoiceState(true)
      }, 45000)
      refs.timeoutIdsRef.current.add(forceResetTimeout as any)

      setInterimUserTranscript('')
      refs.abortRef.current?.abort()
      refs.abortRef.current = new AbortController()

      try {
        const {blob} = await audioActions.stopUtteranceRecording()
        const hasValidInput =
          hadVoiceActivity || hadRealSpeech || pendingUserInterim.trim().length > 5

        if (!hasValidInput || !blob || blob.size === 0) {
          clearTimeout(forceResetTimeout)
          refs.responseRequestedRef.current = false
          setIsAiSpeaking(false)
          setAwaitingResponse(false)
          setStatus('listening')
          refs.inFlightRef.current = false
          unblockListening('no voice activity - resuming', true, true)
          return
        }

        setAwaitingResponse(true)
        const conversationHistory = options.getConversationHistory
          ? options.getConversationHistory()
          : undefined
        const menuSelectedPath = options.getMenuSelectedPath?.() || null
        const result = await sendVoiceChatMessage(blob, {
          signal: refs.abortRef.current.signal,
          conversationHistory,
          voice: options.selectedVoice,
          menuSelectedPath: menuSelectedPath || undefined,
          chatbotMode: options.chatbotMode ?? 'general',
        })
        setAwaitingResponse(false)

        if (result && result.streaming && result.response && result.boundary) {
          const headersObj = (result.response as any).headers
          const headerFiller = headersObj?.get('x-filler-text')
          const headerAnswer = headersObj?.get('x-answer-text')
          const headerWalkthrough = headersObj?.get('x-walkthrough-data')

          // `Headers.get` is case-insensitive, so one lookup per spelling covers both casings.
          applyServerTranscription(
            headersObj?.get('x-transcription-text') || headersObj?.get('x-transcription'),
            true
          )

          if (headerWalkthrough) {
            try {
              const decoded = decodeURIComponent(headerWalkthrough)
              const walkthroughData = JSON.parse(decoded)

              pendingWalkthroughData = walkthroughData
            } catch (e) {
              calculateErrorMessage(e)
            }
          }

          if (headerFiller) {
            const fillerText = decodeHeader(headerFiller)
            audioRefs.fillerTextRef.current = fillerText
            audioActions.stopProgressiveTranscript()
            audioRefs.fillerTranscriptIdRef.current = null
            const id = audioActions.startProgressiveTranscript(fillerText)
            if (id) audioRefs.fillerTranscriptIdRef.current = id
            setTranscriptionStage('filler_transcribing')
          }
          if (headerAnswer) {
            audioRefs.answerTextRef.current = decodeHeader(headerAnswer)
          }

          const stream = result.response.body?.getReader()
          if (!stream) {
            setIsAiSpeaking(false)
            setStatus('listening')
            unblockListening('no stream', true, true)
            return
          }

          const parser = new StreamingMultipartParser(result.boundary)

          let done = false
          while (!done) {
            const {value, done: streamDone} = await stream.read()
            done = streamDone
            if (value) parser.append(value)

            for (const part of parser.parse(done)) {
              const xAudioType = (part.headers['x-audio-type'] || '')
                .toLowerCase()
                .replace(/\s+/g, '-')
              const xWalkthroughData = part.headers['x-walkthrough-data']

              if (xWalkthroughData) {
                try {
                  const decoded = decodeURIComponent(xWalkthroughData)
                  const walkthroughData = JSON.parse(decoded)

                  pendingWalkthroughData = walkthroughData
                } catch (e) {
                  calculateErrorMessage(e)
                }
              }

              const audioBlob = new Blob([part.body as any], {
                type: part.headers['content-type'] || 'audio/mpeg',
              })

              if (xAudioType === 'filler') {
                audioActions.playFillerAudio(audioBlob, (r: string, i?: boolean) =>
                  unblockListening(r, i, true)
                )
                const fillerText = decodeHeader(part.headers['x-answer-text'])
                if (refs.transcriptionEnabledRef.current === true && fillerText) {
                  audioActions.stopProgressiveTranscript()
                  audioRefs.fillerTranscriptIdRef.current = null
                  const id = audioActions.startProgressiveTranscript(fillerText)
                  if (id) audioRefs.fillerTranscriptIdRef.current = id
                }
              } else if (xAudioType === 'full-response') {
                const fullAnswerText = decodeHeader(part.headers['x-answer-text'])
                audioRefs.answerTextRef.current = fullAnswerText
                const completionCallback = (r: string, imm?: boolean) => {
                  if (
                    pendingWalkthroughData &&
                    isWalkthroughEnabled(options.chatbotMode ?? 'general')
                  ) {
                    const event = new CustomEvent('chatbot-walkthrough', {
                      detail: {
                        response: {result: {...pendingWalkthroughData, forceWalkthrough: true}},
                      },
                    })
                    window.dispatchEvent(event)
                    pendingWalkthroughData = null
                  } else if (pendingWalkthroughData) {
                    pendingWalkthroughData = null
                  }
                  unblockListening(r, imm, true)
                }

                if (audioRefs.fillerAudioRef.current || audioRefs.answerAudioToPlayRef.current) {
                  audioRefs.answerAudioToPlayRef.current = audioBlob
                  if (audioRefs.answerCompletionCallbackRef) {
                    audioRefs.answerCompletionCallbackRef.current = completionCallback
                  }
                } else {
                  audioActions.playFullResponseAudio(audioBlob, completionCallback)
                }
              }
            }
          }
          clearTimeout(forceResetTimeout)
          return
        }

        applyServerTranscription(result?.userTranscription)

        const botAnswer = result?.botAnswerText?.trim() || ''
        setStatus('responding')
        setIsAiSpeaking(true)
        if (!refs.listeningBlockedRef.current) blockListening('AI responding', true)

        let transcriptId: string | null = null
        if (refs.transcriptionEnabledRef.current === true && botAnswer) {
          transcriptId = audioActions.startProgressiveTranscript(botAnswer)
        }

        if (result.audioBlob && result.audioBlob.size > 0) {
          audioRefs.answerTextRef.current = botAnswer
          audioRefs.answerTranscriptIdRef.current = transcriptId
          await audioActions.playFullResponseAudio(result.audioBlob, (r: string, i?: boolean) =>
            unblockListening(r, i, true)
          )
        } else {
          const id = window.setTimeout(() => {
            try {
              audioActions.stopProgressiveTranscript()
            } finally {
              refs.timeoutIdsRef.current.delete(id as any)
            }
          }, 1500)
          refs.timeoutIdsRef.current.add(id as any)
        }

        if (refs.inFlightRef.current && !refs.abortRef.current?.signal.aborted) {
          clearTimeout(forceResetTimeout)
          audioActions.stopProgressiveTranscript()
          setIsAiSpeaking(false)
          setAwaitingResponse(false)
          setStatus('listening')
          refs.responseRequestedRef.current = false
          unblockListening('success', false, true)
        }
      } catch (e) {
        calculateErrorMessage(e)
        resetVoiceState(true)
        unblockListening('error in transition', true, true)
      } finally {
        refs.inFlightRef.current = false
      }
    },
    [
      refs,
      blockListening,
      unblockListening,
      resetVoiceState,
      audioActions,
      setStatus,
      setIsAiSpeaking,
      setAwaitingResponse,
      setInterimUserTranscript,
      setTranscriptionStage,
      applyServerTranscription,
      options,
    ]
  )

  return useMemo(
    () => ({
      handleTransitionToResponding,
      resetVoiceState,
      blockListening,
      unblockListening,
    }),
    [handleTransitionToResponding, resetVoiceState, blockListening, unblockListening]
  )
}
